import { useEffect, useRef, type RefObject } from 'react';
import { pushOutsideClickLayer } from './outside-click-stack';

/**
 * Marker attribute for `document.body`-portaled panels in the flow popover
 * system (`ResponsivePopover`'s anchored/sheet roots, and the `FieldPicker`/
 * `PersonValuePicker` menus nested inside them). A portal is a DOM sibling
 * of whatever it's logically nested under — `document.createPortal` doesn't
 * change the React tree, but it does change the real DOM tree, so a plain
 * `ref.contains(event.target)` check (native `mousedown`, not a React
 * synthetic event, so it follows the real DOM) sees a click inside a nested
 * portal as "outside" its portaled ancestor. Without this marker, a
 * mousedown on e.g. a `FieldPicker` menu OPTION — itself portaled to
 * `document.body` because it needs to escape the popover's own scroll box —
 * registers as outside the hosting `ResponsivePopover` and closes it before
 * the option's own `click` handler ever fires, unmounting the option out
 * from under the click and silently dropping the selection.
 *
 * Spread `FLOW_PORTAL_PROPS` onto every portal root in this system;
 * `isInsideFlowPortal` is the corresponding check every outside-click
 * listener here uses instead of (or alongside) a plain ref-contains check.
 */
export const FLOW_PORTAL_ATTR = 'data-flow-portal';
export const FLOW_PORTAL_PROPS = { [FLOW_PORTAL_ATTR]: '' } as const;

/**
 * Glaze components (`SelectField`'s listbox, menus, popovers) portal through
 * base-ui, whose portal root carries `data-base-ui-portal` (the attribute
 * `index.css` pins to the overlay layer). Terra can't spread
 * `FLOW_PORTAL_PROPS` onto those roots, so they count as nested portals too —
 * otherwise picking a Glaze select option inside a `ResponsivePopover` (the
 * Signals Archive popover's reason) closes the popover on mousedown and the
 * outside-click stack swallows the click, dropping the selection.
 */
const BASE_UI_PORTAL_SELECTOR = '[data-base-ui-portal]';

export function isInsideFlowPortal(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(`[${FLOW_PORTAL_ATTR}], ${BASE_UI_PORTAL_SELECTOR}`) !== null;
}

/**
 * Close-on-outside-click helper for small popovers/dropdowns. Treats a click
 * inside any nested `document.body`-portaled flow panel (see
 * `isInsideFlowPortal` above) as "inside" too, so a portaled child menu's
 * option click never closes this popover on the same mousedown.
 *
 * ── F-B: the trigger is "inside" too ─────────────────────────────────────
 * The optional `anchorRef` is the popover's own TRIGGER element. Without it,
 * a mousedown on the trigger of an OPEN popover reads as "outside" (the
 * trigger is a DOM sibling of the panel, not a descendant of it), so this
 * hook fired `onClose()` on `mousedown` — and then the trigger's own
 * `onClick` toggle handler reopened it on the SAME interaction, net effect:
 * the popover could never be closed by clicking the element that opens it.
 * Excluding clicks inside the anchor from the outside-click check leaves
 * that toggle handler as the sole decider for trigger clicks.
 *
 * ── F-C: CAPTURE phase, not bubble (bug, 2026-08-13) ──────────────────────
 * Registered with `{ capture: true }` — load-bearing, not a style choice.
 * A nested portal's own React `onMouseDown` handler (bubble phase, e.g.
 * `MultiValueInput`'s suggestion buttons) can synchronously commit a state
 * update that REMOVES the clicked element from the DOM before the native
 * event finishes dispatching — e.g. picking a suggestion filters it out of
 * the remaining `options` list, so React unmounts that exact `<button>` as
 * part of the same synchronous re-render. A bubble-phase listener on
 * `document` runs AFTER that mutation, so `e.target` is by then a DETACHED
 * node — `.closest()`/`.contains()` walk nothing and report "outside," even
 * though the click was squarely inside a `FLOW_PORTAL_PROPS`-marked panel.
 * Capture-phase listeners run BEFORE any bubble-phase handler (React's synthetic
 * dispatch included), so the DOM is still fully intact when this check runs —
 * eliminating the race regardless of what any bubble handler does to the tree.
 *
 * ── F-D: registers on the shared outside-click STACK, not a raw listener
 * (bug, Spencer report 2026-08-13) ─────────────────────────────────────────
 * A click that lands truly outside EVERYTHING (not inside this panel, not
 * inside a nested picker) while a nested `useDropdownAnchor` picker was open
 * used to fire BOTH this hook's own raw `document` listener and the nested
 * picker's, in one gesture — closing the nested picker AND this whole
 * popover together, discarding an in-progress `FilterPopover` draft the rep
 * only meant to keep editing. `pushOutsideClickLayer` (`outside-click-
 * stack.ts`, mirroring `ui/escape-stack.ts`'s Escape-layering fix) makes only
 * the TOPMOST registered layer evaluate a given click — while a nested
 * picker is open, it's on top, so this hook's callback never even runs for
 * that click; this popover only evaluates outside-clicks once nothing is
 * open above it. `isInsideFlowPortal` stays as a belt-and-braces fallback
 * for any FLOW_PORTAL_PROPS-marked element that, for whatever reason, isn't
 * itself a `useDropdownAnchor`/stack layer.
 *
 * ── F-E: the callback RETURNS whether it closed (bug, Spencer report
 * 2026-08-13, round 2) ─────────────────────────────────────────────────────
 * `ResponsivePopover` has no backdrop, so a dismissing outside click was a
 * REAL click on whatever was actually underneath — closing this popover AND
 * activating that element in one gesture (Spencer's report: a click meant
 * to dismiss Filters also toggled an unrelated sidebar control). Returning
 * `true` here (only when actually closing) tells `outside-click-stack.ts` to
 * swallow this click entirely — accepted trade-off, see that module's doc:
 * switching directly between sibling toolbar popovers (e.g. clicking
 * "Columns" while "Filters" is open) now takes two clicks instead of one,
 * same as dismissing onto any other element.
 */
export function useClickOutside(
  open: boolean,
  onClose: () => void,
  anchorRef?: RefObject<HTMLElement | null>,
) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    return pushOutsideClickLayer((target) => {
      const insidePanel = ref.current !== null && ref.current.contains(target);
      const insideAnchor = anchorRef?.current != null && anchorRef.current.contains(target);
      if (!insidePanel && !insideAnchor && !isInsideFlowPortal(target)) {
        onClose();
        return true;
      }
      return false;
    });
  }, [open, onClose, anchorRef]);
  return ref;
}
